import { Registrant } from "../../../types/types";
import { formMappings } from "../../../config/formMappings";
import { useSelector } from "react-redux";
import { RootState } from "../../../store";

// Helper function to get field value, trying both v1 and v2 field names
function getFieldValue(registrant: any, v1Field: string, v2Field: string): any {
  return registrant[v2Field] ?? registrant[v1Field] ?? "";
}

export default function RegistrantCard(props: {
  registrant: Registrant | any;
  idx: number;
  entry?: any; // Optional entry (kept for backward compatibility but not used)
}) {
  // Get form mapping from Redux state
  const activeFormName = useSelector(
    (state: RootState) => state.activeForm.activeFormName
  );
  const formMapping = formMappings[activeFormName as keyof typeof formMappings];
  const isVersion2 = formMapping?.version === "2";

  // Get field values, supporting both v1 and v2 field names
  const checkedIn = getFieldValue(props.registrant, "opjkc3", "opjkc32");
  const firstName = getFieldValue(props.registrant, "s7ztq3", "s7ztq32");
  const lastName = getFieldValue(props.registrant, "pu0bc3", "pu0bc32");
  const gender = getFieldValue(props.registrant, "85tfd3", "85tfd32");
  const age = getFieldValue(props.registrant, "i6b3v3", "i6b3v32");
  const email = getFieldValue(props.registrant, "jgfy3", "jgfy32");
  const registrationType = getFieldValue(props.registrant, "5yusq3", "5yusq32");
  const participation = getFieldValue(props.registrant, "bpc1r3", "bpc1r32");

  // Get ethnicity for v2 (field name is "b3bic3" for both versions, but only in registrant for v2)
  const ethnicity = isVersion2
    ? getFieldValue(props.registrant, "b3bic3", "b3bic3")
    : null;

  return (
    <section aria-labelledby={`registrant-${props.idx + 1}-information-title`}>
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:px-6">
          <h2
            id="registrant-information-title"
            className="text-lg/6 font-medium text-gray-900"
          >
            Registrant {props.idx + 1} Information
            {checkedIn === "true" ? (
              <span className="ml-3 inline-flex items-center rounded-md bg-green-50 px-2 py-1 text-xs font-medium text-green-700 ring-1 ring-inset ring-green-600/20">
                Checked-In
              </span>
            ) : (
              <span className=" ml-3 inline-flex items-center rounded-md bg-gray-50 px-2 py-1 text-xs font-medium text-gray-600 ring-1 ring-inset ring-gray-500/10">
                Not Checked-In
              </span>
            )}
          </h2>
          <p className="mt-1 max-w-2xl text-sm text-gray-500">
            Personal details for the registrant.
          </p>
        </div>
        <div className="border-t border-gray-200 px-4 py-5 sm:px-6">
          <dl className="grid grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-3">
            <div className="sm:col-span-1">
              <dt className="text-sm font-medium text-gray-500">Full Name</dt>
              <dd className="mt-1 text-lg text-gray-900">{`${firstName} ${lastName}`}</dd>
            </div>
            <div className="sm:col-span-1">
              <dt className="text-sm font-medium text-gray-500">Gender</dt>
              <dd className="mt-1 text-lg text-gray-900">{gender}</dd>
            </div>
            <div className="sm:col-span-1">
              <dt className="text-sm font-medium text-gray-500">Age</dt>
              <dd className="mt-1 text-lg text-gray-900">{age}</dd>
            </div>
            <div className="sm:col-span-1">
              <dt className="text-sm font-medium text-gray-500">
                Email address
              </dt>
              <dd className="mt-1 text-lg text-gray-900">
                <a href={`mailto:${email}`} target="_blank" rel="noreferrer">
                  {email}
                </a>
              </dd>
            </div>
            <div className="sm:col-span-1">
              <dt className="text-sm font-medium text-gray-500">
                Registration Type
              </dt>
              <dd className="mt-1 text-lg text-gray-900">{registrationType}</dd>
            </div>
            {isVersion2 && ethnicity && (
              <div className="sm:col-span-1">
                <dt className="text-sm font-medium text-gray-500">Ethnicity</dt>
                <dd className="mt-1 text-gray-900">
                  <span className="inline-flex items-center rounded-md bg-gray-50 px-2 py-1 text-md font-medium text-gray-600 ring-1 ring-inset ring-gray-500/10">
                    {ethnicity}
                  </span>
                </dd>
              </div>
            )}
            <div className="sm:col-span-3">
              <dt className="text-sm font-medium text-gray-500">
                Registrant Participation
              </dt>
              <dd className="mt-1 text-gray-900">
                {participation &&
                (Array.isArray(participation)
                  ? participation.length > 0
                  : participation !== "") ? (
                  (Array.isArray(participation)
                    ? participation
                    : [participation]
                  ).map((part: string, idx: number) => (
                    <span
                      key={idx}
                      className="inline-flex items-center rounded-md bg-gray-50 px-2 py-1 text-md font-medium text-gray-600 ring-1 ring-inset ring-gray-500/10"
                    >
                      {part}
                    </span>
                  ))
                ) : (
                  <p className="italic text-lg">None</p>
                )}
              </dd>
            </div>
          </dl>
        </div>
      </div>
    </section>
  );
}
